/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useGlobalFilter, usePagination, useSortBy, useTable } from "react-table";
import { Stack } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import TableCommon from "components/TableCommon";

function DataTable({
  table,
  loading,
  syncing,
  filters,
  paginationData,
  setPaginationData,
  onFetchData,
  onSync,
  query,
  setQuery,
}) {
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { setPageSize } = tableInstance;

  useEffect(() => {
    setPageSize(paginationData.size);
  }, [paginationData.size, setPageSize]);

  const [localFilters, setLocalFilters] = useState(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleApply = () => {
    onFetchData(localFilters);
  };

  const handleReset = () => {
    const currentYear = String(new Date().getFullYear());
    const currentMonth = String(new Date().getMonth() + 1);
    const resetFilters = {
      year: currentYear,
      month: currentMonth,
      fromDate: "",
      toDate: "",
      searchBy: "",
    };

    setLocalFilters(resetFilters);
    onFetchData(resetFilters);
  };

  return (
    <>
      <SoftBox
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="flex-start"
        p={3}
        pb={1}
      >
        <SoftBox lineHeight={1} width="100%">
          <Stack spacing={2} direction="row" flexWrap="wrap">
            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Năm
              </SoftTypography>
              <SoftBox width="8rem">
                <SoftInput
                  placeholder="YYYY"
                  value={localFilters.year}
                  onChange={({ currentTarget }) => {
                    const next = currentTarget.value.replace(/\D/g, "").slice(0, 4);
                    setLocalFilters((prev) => ({ ...prev, year: next }));
                  }}
                />
              </SoftBox>
            </SoftBox>

            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Tháng
              </SoftTypography>
              <SoftBox width="6rem">
                <SoftInput
                  placeholder="MM"
                  value={localFilters.month}
                  onChange={({ currentTarget }) => {
                    const next = currentTarget.value.replace(/\D/g, "").slice(0, 2);
                    if (Number(next) <= 12) {
                      setLocalFilters((prev) => ({ ...prev, month: next }));
                    }
                  }}
                />
              </SoftBox>
            </SoftBox>

            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Từ ngày
              </SoftTypography>
              <SoftInput
                type="date"
                value={localFilters.fromDate}
                onChange={({ currentTarget }) =>
                  setLocalFilters((prev) => ({ ...prev, fromDate: currentTarget.value }))
                }
              />
            </SoftBox>

            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Đến ngày
              </SoftTypography>
              <SoftInput
                type="date"
                value={localFilters.toDate}
                onChange={({ currentTarget }) =>
                  setLocalFilters((prev) => ({ ...prev, toDate: currentTarget.value }))
                }
              />
            </SoftBox>

            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Tìm kiếm
              </SoftTypography>
              <SoftBox width="16rem">
                <SoftInput
                  placeholder="Đơn vị, nội dung..."
                  value={localFilters.searchBy}
                  onChange={({ currentTarget }) =>
                    setLocalFilters((prev) => ({ ...prev, searchBy: currentTarget.value }))
                  }
                />
              </SoftBox>
            </SoftBox>

            <SoftBox display="flex" alignItems="flex-end">
              <Stack direction="row" spacing={1}>
                <SoftBox
                  component="button"
                  onClick={handleApply}
                  disabled={loading}
                  sx={{
                    height: "39px",
                    padding: "0 16px",
                    borderRadius: 0,
                    border: "none",
                    backgroundColor: "#1a73e8",
                    color: "white",
                    cursor: "pointer",
                    fontWeight: "bold",
                    opacity: loading ? 0.5 : 1,
                    "&:hover": { backgroundColor: "#1557b0" },
                  }}
                >
                  Tải dữ liệu
                </SoftBox>
                <SoftBox
                  component="button"
                  onClick={handleReset}
                  disabled={loading || syncing}
                  sx={{
                    height: "39px",
                    padding: "0 16px",
                    borderRadius: 0,
                    border: "1px solid #d2d6da",
                    backgroundColor: "white",
                    color: "#344767",
                    cursor: "pointer",
                    fontWeight: "bold",
                    opacity: loading || syncing ? 0.5 : 1,
                    "&:hover": { backgroundColor: "#f8f9fa" },
                  }}
                >
                  Reset
                </SoftBox>
                <SoftBox
                  component="button"
                  onClick={onSync}
                  disabled={syncing || loading}
                  sx={{
                    height: "39px",
                    padding: "0 16px",
                    borderRadius: 0,
                    border: "none",
                    backgroundColor: syncing ? "#ccc" : "#4CAF50",
                    color: "white",
                    cursor: "pointer",
                    fontWeight: "bold",
                    opacity: syncing || loading ? 0.5 : 1,
                    "&:hover": { backgroundColor: "#45a049" },
                  }}
                >
                  {syncing ? "Đang đồng bộ..." : "Đồng bộ dữ liệu"}
                </SoftBox>
              </Stack>
            </SoftBox>
          </Stack>
        </SoftBox>
      </SoftBox>

      <TableCommon
        loading={loading}
        query={query}
        setQuery={setQuery}
        paginationData={paginationData}
        setPaginationData={setPaginationData}
        table={table}
        isSorted={true}
        noEndBorder={false}
        pagination={{ variant: "gradient", color: "info" }}
        tableInstance={tableInstance}
      />
    </>
  );
}

DataTable.propTypes = {
  table: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  syncing: PropTypes.bool,
  filters: PropTypes.object.isRequired,
  paginationData: PropTypes.object.isRequired,
  setPaginationData: PropTypes.func.isRequired,
  onFetchData: PropTypes.func.isRequired,
  onSync: PropTypes.func.isRequired,
  query: PropTypes.object.isRequired,
  setQuery: PropTypes.func.isRequired,
};

export default DataTable;
